import { REGULARISATION_PATHS } from "../../../lib/compta/regularisationsNav";

export type ComptaSecondaryId = "saisie" | "revision" | "fiscalite" | "etats" | "dossier";

/** Section principale Compta active selon l’URL (mode Compta uniquement). */
export function comptaSectionFromPath(pathname: string): ComptaSecondaryId | null {
  if (
    pathname.startsWith("/compta/factures-achat") ||
    pathname.startsWith("/compta/factures-vente") ||
    pathname.startsWith("/compta/transactions") ||
    pathname.startsWith("/compta/journaux") ||
    pathname.startsWith("/compta/saisie") ||
    pathname.startsWith("/compta/import-balance") ||
    pathname.startsWith("/compta/import-fec") ||
    pathname.startsWith("/gestion/produits/import")
  ) {
    return "saisie";
  }
  if (
    pathname.startsWith("/compta/revision") ||
    pathname.startsWith("/compta/dossier-revision") ||
    pathname.startsWith("/compta/lettrage") ||
    pathname.startsWith("/compta/regularisations") ||
    pathname.startsWith("/compta/cloture-exercice")
  ) {
    return "revision";
  }
  if (pathname.startsWith("/compta/fiscalite")) return "fiscalite";
  if (pathname === "/bilan" || pathname.startsWith("/compta/etats-financiers")) return "etats";
  if (pathname.startsWith("/compta/dossier-client")) return "dossier";
  return null;
}
export type GestionSecondaryId = "facturation" | "factures-achat";

export type SecondaryNavItem = {
  path: string;
  label: string;
  description: string;
  /** État passé à react-router (ex. navigation depuis Compta). */
  navState?: { navFrom?: string };
};

export const COMPTA_SUBMENU_SAISIE: SecondaryNavItem[] = [
  { path: "/compta/factures-achat", label: "Factures fournisseurs", description: "Saisie et validation côté compta" },
  { path: "/compta/factures-vente", label: "Factures clients", description: "Facturation comptable" },
  { path: "/compta/transactions", label: "Transactions bancaires", description: "Rapprochement et affectation" },
  { path: "/compta/journaux", label: "Journaux", description: "Consultation et saisie par journal" },
  { path: "/compta/saisie/masse", label: "Saisie de masse", description: "Saisir des écritures à la main" },
  { path: "/compta/import-balance", label: "Import balance", description: "Import du plan et des soldes" },
  { path: "/compta/import-fec", label: "Import FEC", description: "Fichier des écritures comptables" },
  {
    path: "/gestion/produits/import",
    label: "Imports catalogue",
    description: "Articles et catalogues",
    navState: { navFrom: "compta" },
  },
];

export const COMPTA_SUBMENU_REVISION: SecondaryNavItem[] = [
  { path: "/compta/revision/balance", label: "Balance générale", description: "Synthèse des soldes" },
  { path: "/compta/revision/balance-fournisseurs", label: "Balance fournisseurs", description: "Dettes fournisseurs" },
  { path: "/compta/revision/balance-clients", label: "Balance clients", description: "Créances clients" },
  { path: "/compta/revision/balance-agee", label: "Balance âgée", description: "Ancienneté des créances" },
  { path: "/compta/revision/grand-livre", label: "Grand livre", description: "Consultation des comptes" },
  { path: "/compta/lettrage", label: "Lettrage", description: "Rapprochement des comptes tiers" },
  { path: "/compta/revision/rapprochement", label: "Rapprochement bancaire", description: "Pointage trésorerie" },
  { path: "/compta/revision/feuilles", label: "Feuilles de travail", description: "Dossier de révision et diligences" },
  {
    path: "/compta/dossier-revision",
    label: "Dossier de clôture",
    description: "Fiche dossier, trésorerie, fournisseurs…",
  },
  { path: "/compta/revision/immobilisations", label: "Immobilisations", description: "Actifs et amortissements" },
  { path: REGULARISATION_PATHS.cca, label: "Régularisations", description: "CCA, FNP, PCA, FAE…" },
  { path: "/compta/cloture-exercice", label: "Clôture d'exercice", description: "Validation et clôture" },
];

export const COMPTA_SUBMENU_FISCALITE: SecondaryNavItem[] = [
  { path: "/compta/fiscalite", label: "Fiscalité", description: "Vue d'ensemble" },
  { path: "/compta/fiscalite/tva-board", label: "Tableau TVA", description: "Suivi des déclarations" },
  { path: "/compta/fiscalite/tva", label: "Déclaration TVA", description: "Saisie et validation" },
];

export const COMPTA_SUBMENU_ETATS: SecondaryNavItem[] = [
  { path: "/bilan", label: "Bilan & résultat", description: "Vue financière synthétique" },
  { path: "/compta/etats-financiers", label: "États financiers", description: "Bilan, résultat, annexes" },
];

export const COMPTA_SUBMENU_DOSSIER: SecondaryNavItem[] = [
  { path: "/compta/dossier-client/ged", label: "GED", description: "Documents du dossier" },
  { path: "/parametres/entreprise", label: "Informations entreprise", description: "Fiche et paramètres légaux" },
];

export type SecondaryPanelKind = ComptaSecondaryId | GestionSecondaryId | "parametres";

export const SECONDARY_TITLES: Record<SecondaryPanelKind, { title: string; subtitle: string }> = {
  saisie: { title: "Saisie", subtitle: "Opérations comptables courantes" },
  revision: { title: "Révision", subtitle: "Contrôles et balances" },
  fiscalite: { title: "Fiscalité", subtitle: "TVA et obligations" },
  etats: { title: "États financiers", subtitle: "États de synthèse" },
  dossier: { title: "Dossier client", subtitle: "Pièces et informations" },
  facturation: { title: "Facturation", subtitle: "Factures clients" },
  "factures-achat": { title: "Factures d'achat", subtitle: "Achats & fournisseurs" },
  parametres: { title: "Paramètres", subtitle: "Configuration du dossier" },
};

export function itemsForSecondary(kind: SecondaryPanelKind | null): SecondaryNavItem[] | null {
  if (!kind || kind === "parametres") return null;
  switch (kind) {
    case "saisie":
      return COMPTA_SUBMENU_SAISIE;
    case "revision":
      return COMPTA_SUBMENU_REVISION;
    case "fiscalite":
      return COMPTA_SUBMENU_FISCALITE;
    case "etats":
      return COMPTA_SUBMENU_ETATS;
    case "dossier":
      return COMPTA_SUBMENU_DOSSIER;
    default:
      return null;
  }
}
